// pages/OurPolicies.jsx
import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Sparkles, BookOpen, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

const API_BASE_URL = "https://backend.dlapakistan.com/api/ourpolicies";

const OurPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyTitle, setPolicyTitle] = useState("");
  const [policySlug, setPolicySlug] = useState("");
  const [editingPolicy, setEditingPolicy] = useState(null);

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemTitle, setItemTitle] = useState("");
  const [itemSlug, setItemSlug] = useState("");
  const [activePolicyId, setActivePolicyId] = useState(null);
  const [activePolicySlug, setActivePolicySlug] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  const [showSuccessModel, setShowSuccessModel] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const navigate = useNavigate();
  const emerald600 = "#059669";

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/`);
      setPolicies(response.data.map(policy => ({
        ...policy,
        showItems: false,
        items: policy.items || []
      })));
      setError(null);
    } catch (err) {
      console.error("Error fetching policies:", err);
      setError("Failed to load policies. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");

  // Policy CRUD Functions
  const addPolicy = async () => {
    if (!policyTitle.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a policy title',
        confirmButtonColor: emerald600,
      });
      return;
    }
    
    try {
      const newPolicy = {
        title: policyTitle,
        heading: "",
        content: "",
        is_active: true,
        priority: 0
      };

      const response = await axios.post(`${API_BASE_URL}/`, newPolicy);
      
      setPolicies([
        ...policies,
        {
          ...response.data,
          showItems: false,
          items: []
        }
      ]);
      
      setSuccessMessage("🎉 Policy Created Successfully!");
      setShowSuccessModel(true);
      
      setPolicyTitle("");
      setPolicySlug("");
      setShowPolicyModal(false);
      setEditingPolicy(null);
    } catch (err) {
      console.error("Error adding policy:", err);
      const errorMsg = err.response?.data?.title?.[0] || 
                       err.response?.data?.error || 
                       "Failed to add policy. Please try again.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        confirmButtonColor: emerald600,
      });
    }
  };

  const editPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyTitle(policy.title);
    setPolicySlug(policy.slug);
    setShowPolicyModal(true);
  };

  const updatePolicy = async () => {
    if (!policyTitle.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a policy title',
        confirmButtonColor: emerald600,
      });
      return;
    }
    
    try {
      const updatedPolicy = {
        title: policyTitle,
        heading: editingPolicy.heading,
        content: editingPolicy.content,
        is_active: editingPolicy.is_active,
        priority: editingPolicy.priority
      };

      await axios.put(`${API_BASE_URL}/${editingPolicy.slug}/`, updatedPolicy);
      
      setPolicies(policies.map(p => 
        p.id === editingPolicy.id 
          ? { ...p, title: policyTitle, slug: generateSlug(policyTitle) }
          : p
      ));
      
      setSuccessMessage("✅ Policy Updated Successfully!");
      setShowSuccessModel(true);
      
      setPolicyTitle("");
      setPolicySlug("");
      setShowPolicyModal(false);
      setEditingPolicy(null);
    } catch (err) {
      console.error("Error updating policy:", err);
      const errorMsg = err.response?.data?.title?.[0] || 
                       err.response?.data?.error || 
                       "Failed to update policy. Please try again.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        confirmButtonColor: emerald600,
      });
    }
  };

  const deletePolicy = async (policy) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${policy.title}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE_URL}/${policy.slug}/`);
        
        setPolicies(policies.filter(p => p.id !== policy.id));
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Policy has been deleted.',
          confirmButtonColor: emerald600,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error("Error deleting policy:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete policy. Please try again.',
          confirmButtonColor: emerald600,
        });
      }
    }
  };

  const toggleItems = async (policySlug) => {
    const policy = policies.find(p => p.slug === policySlug);
    const shouldShowItems = !policy.showItems;
    
    setPolicies(
      policies.map((p) =>
        p.slug === policySlug ? { ...p, showItems: shouldShowItems } : p
      )
    );

    if (shouldShowItems && (!policy.items || policy.items.length === 0)) {
      try {
        const response = await axios.get(`${API_BASE_URL}/${policySlug}/items/`);
        
        setPolicies(
          policies.map((p) =>
            p.slug === policySlug ? { 
              ...p, 
              items: response.data,
              showItems: true
            } : p
          )
        );
      } catch (err) {
        console.error("Error fetching policy items:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load items. Please try again.',
          confirmButtonColor: emerald600,
        });
        
        setPolicies(
          policies.map((p) =>
            p.slug === policySlug ? { ...p, showItems: false } : p
          )
        );
      }
    }
  };

  // Item CRUD Functions
  const openItemModal = (policyId, policySlug, item = null) => {
    setActivePolicyId(policyId);
    setActivePolicySlug(policySlug);
    if (item) {
      setEditingItem(item);
      setItemTitle(item.title);
      setItemSlug(item.slug);
    } else {
      setEditingItem(null);
      setItemTitle("");
      setItemSlug("");
    }
    setShowItemModal(true);
  };

  const addItem = async () => {
    if (!itemTitle.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter an item title',
        confirmButtonColor: emerald600,
      });
      return;
    }
    
    if (!activePolicySlug) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Policy not selected',
        confirmButtonColor: emerald600,
      });
      return;
    }
    
    try {
      const newItem = {
        title: itemTitle,
        heading: "",
        content: "",
        is_active: true,
        order: 0
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/${activePolicySlug}/items/`,
        newItem
      );
      
      setPolicies(
        policies.map((p) =>
          p.slug === activePolicySlug
            ? { 
                ...p, 
                items: [...(p.items || []), response.data]
              }
            : p
        )
      );
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Section added successfully!',
        confirmButtonColor: emerald600,
        timer: 2000,
        showConfirmButton: false,
      });
      
      setItemTitle("");
      setItemSlug("");
      setShowItemModal(false);
      setActivePolicySlug("");
      setEditingItem(null);
    } catch (err) {
      console.error("Error adding item:", err);
      const errorMsg = err.response?.data?.title?.[0] || 
                       err.response?.data?.error || 
                       err.response?.data?.detail ||
                       "Failed to add item. Please try again.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        confirmButtonColor: emerald600,
      });
    }
  };

  const updateItem = async () => {
    if (!itemTitle.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter an item title',
        confirmButtonColor: emerald600,
      });
      return;
    }
    
    if (!activePolicySlug || !editingItem) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Policy or item not selected',
        confirmButtonColor: emerald600,
      });
      return;
    }
    
    try {
      const updatedItem = {
        title: itemTitle,
        heading: editingItem.heading,
        content: editingItem.content,
        is_active: editingItem.is_active,
        order: editingItem.order
      };
      
      await axios.put(
        `${API_BASE_URL}/${activePolicySlug}/items/${editingItem.slug}/`,
        updatedItem
      );
      
      setPolicies(
        policies.map((p) =>
          p.slug === activePolicySlug
            ? { 
                ...p, 
                items: p.items.map(item => 
                  item.id === editingItem.id 
                    ? { ...item, title: itemTitle, slug: generateSlug(itemTitle) }
                    : item
                )
              }
            : p
        )
      );
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Section updated successfully!',
        confirmButtonColor: emerald600,
        timer: 2000,
        showConfirmButton: false,
      });
      
      setItemTitle("");
      setItemSlug("");
      setShowItemModal(false);
      setActivePolicySlug("");
      setEditingItem(null);
    } catch (err) {
      console.error("Error updating item:", err);
      const errorMsg = err.response?.data?.title?.[0] || 
                       err.response?.data?.error || 
                       err.response?.data?.detail ||
                       "Failed to update item. Please try again.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        confirmButtonColor: emerald600,
      });
    }
  };

  const deleteItem = async (policySlug, item) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${item.title}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE_URL}/${policySlug}/items/${item.slug}/`);
        
        setPolicies(
          policies.map((p) =>
            p.slug === policySlug
              ? { 
                  ...p, 
                  items: p.items.filter(i => i.id !== item.id)
                }
              : p
          )
        );
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Section has been deleted.',
          confirmButtonColor: emerald600,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error("Error deleting item:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete section. Please try again.',
          confirmButtonColor: emerald600,
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full py-10 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading policies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full py-10 flex justify-center items-center">
        <div className="text-center text-red-600">
          <p className="text-lg font-medium mb-4">{error}</p>
          <button
            onClick={fetchPolicies}
            style={{ backgroundColor: emerald600 }}
            className="px-6 py-2.5 rounded-2xl text-white font-medium hover:opacity-90 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full py-10 flex justify-center relative overflow-hidden"
      style={{ backgroundColor: "transparent" }}
    >
      <div className="w-full max-w-[95%] px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
          <h1
            className="text-3xl font-bold"
            style={{ color: emerald600 }}
          >
            Our Policies
          </h1>
          <button
            onClick={() => {
              setEditingPolicy(null);
              setPolicyTitle("");
              setPolicySlug("");
              setShowPolicyModal(true);
            }}
            style={{ backgroundColor: emerald600 }}
            className="text-white px-6 py-2.5 rounded-2xl font-medium hover:opacity-90 transition-all flex items-center gap-2 hover:scale-105 transform  "
          >
            <Sparkles size={18} />
            + Add Policy
          </button>
        </div>

        {/* Empty State */}
        {policies.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center rounded-3xl"
            style={{
              border: "4px dashed #d1d5db",
              width: "100%",
              maxWidth: "1400px",
              margin: "0 auto",
              padding: "6rem 2rem",
              minHeight: "400px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-20 w-20 text-gray-300 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 20l9-8-9-8-9 8 9 8z"
              />
            </svg>
            <p className="text-gray-400 text-lg mb-6">No policies added yet.</p>
            <button
              onClick={() => {
                setEditingPolicy(null);
                setPolicyTitle("");
                setPolicySlug("");
                setShowPolicyModal(true);
              }}
              style={{ backgroundColor: emerald600 }}
              className="px-6 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-all hover:scale-105 transform  "
            >
              Add Your First Policy
            </button>
          </div>
        ) : (
          /* Policies List */
          <div className="space-y-6">
            {policies.map((policy) => (
              <div
                key={policy.id}
                className="bg-white/80 backdrop-blur-md rounded-3xl p-8 border border-gray-100 animate-fadeIn hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h2
                      className="text-2xl font-bold cursor-pointer text-[#70B444] hover:underline hover:scale-105 transform   inline-block"
                      onClick={() => navigate(`/About-Dla/OurPolicies/${policy.slug}`)}
                    >
                      {policy.title}
                    </h2>
                    <p className="text-xs text-gray-400 mt-1">/{policy.slug}</p>
                    {policy.heading && (
                      <p className="text-gray-600 mt-2 text-sm">{policy.heading}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Edit Policy Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editPolicy(policy);
                      }}
                      className="p-2 rounded-full hover:bg-emerald-100 text-emerald-600 hover:text-emerald-700 transition-all hover:scale-110 transform  "
                      title="Edit Policy"
                    >
                      <Edit size={18} />
                    </button>

                    {/* Delete Policy Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePolicy(policy);
                      }}
                      className="p-2 rounded-full hover:bg-red-100 text-red-600 hover:text-red-700 transition-all hover:scale-110 transform  "
                      title="Delete Policy"
                    >
                      <Trash2 size={18} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItems(policy.slug);
                      }}
                      className="p-1 rounded-full hover:bg-gray-200 transition-all hover:scale-110 transform transition-transform duration-300 "
                      title="Toggle Sections"
                    >
                      {policy.showItems ? (
                        <ChevronUp size={22} />
                      ) : (
                        <ChevronDown size={22} />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openItemModal(policy.id, policy.slug);
                      }}
                      className="text-sm bg-white/60 hover:bg-white/70 px-5 py-2 rounded-2xl border border-gray-200 transition-all text-[#70B444] font-medium hover:scale-105 transform flex items-center gap-2"
                    >
                      <Sparkles size={14} />
                      Add Section
                    </button>
                  </div>
                </div>

                {/* Items */}
                {policy.showItems && (
                  <div className="mt-6 animate-slideDown">
                    {policy.items && policy.items.length > 0 ? (
                      <ul className="space-y-3">
                        {policy.items.map((item) => (
                          <li
                            key={item.id}
                            className="text-lg text-gray-700 bg-white/60 backdrop-blur-md p-4 rounded-2xl hover:scale-[1.02]   duration-300 hover:shadow-md"
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <span
                                  className="text-[#70B444] font-medium hover:underline cursor-pointer hover:scale-105 transform   inline-block"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/About-Dla/OurPolicies/${policy.slug}/items/${item.slug}`);
                                  }}
                                >
                                  {item.title}
                                </span>
                                <span className="text-gray-400 text-sm ml-3">
                                  /{item.slug}
                                </span>
                                {item.heading && (
                                  <p className="text-gray-500 text-sm mt-1">
                                    {item.heading}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2 ml-4">
                                {/* Edit Item Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openItemModal(policy.id, policy.slug, item);
                                  }}
                                  className="p-1.5 rounded-full hover:bg-emerald-100 text-emerald-600 hover:text-emerald-700 transition-all hover:scale-110 transform "
                                  title="Edit Section"
                                >
                                  <Edit size={16} />
                                </button>

                                {/* Delete Item Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteItem(policy.slug, item);
                                  }}
                                  className="p-1.5 rounded-full hover:bg-red-100 text-red-600 hover:text-red-700 transition-all hover:scale-110 transform  "
                                  title="Delete Section"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-400 text-sm mt-2 ml-1">
                        No sections added yet.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Policy Modal */}
      {showPolicyModal && (
        <Modal
          title={editingPolicy ? "Edit Policy" : "Add Policy"}
          onClose={() => {
            setShowPolicyModal(false);
            setPolicyTitle("");
            setPolicySlug("");
            setEditingPolicy(null);
          }}
          emerald600={emerald600}
        >
          <input
            type="text"
            placeholder="Policy title"
            value={policyTitle}
            onChange={(e) => {
              const val = e.target.value;
              setPolicyTitle(val);
              setPolicySlug(generateSlug(val));
            }}
            className="w-full border border-gray-200 rounded-2xl px-5 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-[#70B444] transition-all focus:scale-105 transform   duration-300"
          />
          {policyTitle && (
            <div className="w-full border border-gray-200 rounded-2xl px-5 py-3 mb-5 bg-gray-50 animate-pulse">
              <p className="text-sm text-gray-500">
                <span className="font-medium">Slug:</span> {policySlug}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Note: Policy heading and content can be added in the detail page
              </p>
            </div>
          )}
          <ModalActions
            onCancel={() => {
              setShowPolicyModal(false);
              setPolicyTitle("");
              setPolicySlug("");
              setEditingPolicy(null);
            }}
            onAdd={editingPolicy ? updatePolicy : addPolicy}
            emerald600={emerald600}
            buttonText={editingPolicy ? "Update" : "Add"}
          />
        </Modal>
      )}

      {/* Add/Edit Item Modal */}
      {showItemModal && (
        <Modal
          title={`${editingItem ? 'Edit' : 'Add'} Section to Policy: ${activePolicySlug}`}
          onClose={() => {
            setShowItemModal(false);
            setItemTitle("");
            setItemSlug("");
            setActivePolicySlug("");
            setEditingItem(null);
          }}
          emerald600={emerald600}
        >
          <div className="mb-4 p-3 bg-blue-50 rounded-xl">
            <p className="text-sm text-blue-700">
              <strong>Policy:</strong> {activePolicySlug}
            </p>
          </div>
          <input
            type="text"
            placeholder="Section title"
            value={itemTitle}
            onChange={(e) => {
              const val = e.target.value;
              setItemTitle(val);
              setItemSlug(generateSlug(val));
            }}
            className="w-full border border-gray-200 rounded-2xl px-5 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-[#70B444] transition-all focus:scale-105 transform   duration-300"
          />
          {itemTitle && (
            <div className="w-full border border-gray-200 rounded-2xl px-5 py-3 mb-5 bg-gray-50">
              <p className="text-sm text-gray-500">
                <span className="font-medium">Slug:</span> {itemSlug}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Note: Section heading and content can be added in the detail page
              </p>
            </div>
          )}
          <ModalActions
            onCancel={() => {
              setShowItemModal(false);
              setItemTitle("");
              setItemSlug("");
              setActivePolicySlug("");
              setEditingItem(null);
            }}
            onAdd={editingItem ? updateItem : addItem}
            emerald600={emerald600}
            buttonText={editingItem ? "Update" : "Add"}
          />
        </Modal>
      )}

      {/* Success Model */}
      {showSuccessModel && (
        <AnimatedModel
          message={successMessage}
          description="Your policy has been successfully saved!"
          onClose={() => setShowSuccessModel(false)}
          emerald600={emerald600}
        />
      )}

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: .5;
          }
        }
      `}</style>
    </div>
  );
};

const Modal = ({ title, children, onClose, emerald600 }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    ></div>
    <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 w-full max-w-3xl z-50 pointer-events-auto animate-fadeIn border border-gray-100 shadow-2xl">
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-xl font-bold">{title}</h3>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-200 rounded-full transition-colors hover:rotate-90 transform   duration-300"
        >
          ✕
        </button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, onAdd, emerald600, buttonText = "Add" }) => (
  <div className="flex justify-end gap-3 mt-4">
    <button
      onClick={onCancel}
      className="px-5 py-2 border rounded-2xl hover:bg-gray-100 transition-all hover:scale-105 transform   duration-300"
    >
      Cancel
    </button>
    <button
      onClick={onAdd}
      style={{ backgroundColor: emerald600 }}
      className="px-5 py-2 text-white rounded-2xl hover:brightness-110 transition-all hover:scale-105 transform   duration-300 flex items-center gap-2"
    >
      <Sparkles size={16} />
      {buttonText}
    </button>
  </div>
);

const AnimatedModel = ({ message, description, onClose, emerald600 }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 backdrop-blur-md rounded-3xl p-8 w-full max-w-lg z-50 pointer-events-auto animate-fadeIn border-2 border-emerald-300 shadow-2xl transform hover:scale-105   duration-500 relative overflow-hidden">
        
        <div className="flex flex-col items-center text-center relative z-10">
          <div className="mb-6 animate-icon-entrance">
            <BookOpen size={80} className="text-emerald-600" />
          </div>
          
          <h3 className="text-2xl font-bold mb-3 text-gray-800">{message}</h3>
          
          <p className="text-gray-600 mb-6">{description}</p>
          
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-2xl font-medium transition-all hover:scale-105 transform   duration-300 text-white shadow-lg hover:shadow-emerald-200/50 hover:shadow-xl relative overflow-hidden group bg-emerald-500 hover:bg-emerald-600"
          >
            <span className="relative z-10">Awesome! 🎉</span>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-emerald-600 to-emerald-500"></div>
          </button>
        </div>

        {/* Simple Confetti Effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                background: `linear-gradient(45deg, ${emerald600}, #10b981)`,
                animation: `confetti-fall ${Math.random() * 2 + 1}s linear ${Math.random() * 0.5}s forwards`,
              }}
            />
          ))}
        </div>

        <style jsx>{`
          @keyframes confetti-fall {
            0% {
              transform: translateY(-100px) translateX(0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(500px) translateX(${Math.random() * 200 - 100}px) rotate(${Math.random() * 360}deg);
              opacity: 0;
            }
          }
          
          @keyframes icon-entrance {
            0% {
              transform: scale(0) rotate(-180deg);
              opacity: 0;
            }
            70% {
              transform: scale(1.2) rotate(20deg);
              opacity: 1;
            }
            100% {
              transform: scale(1) rotate(0deg);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OurPolicies;